/**
 * Commerce properties for analytics events — `price`, `price_currency`,
 * `click_id`, `short_id`.
 *
 * Born out of the 2026-08 lifecycle discovery
 * (docs/features/active/lifecycle-event-properties/): BMO's Event Spec assumed
 * `result_click_out` already carried a price and a click identifier. Neither
 * existed. The click event carried the link, merchant, title, position and a
 * partner flag — enough to know *what* was clicked, nothing to know what it
 * cost or whether it earned anything.
 *
 * Two separate problems, two separate answers:
 *
 * - **Price** is already in the payload, just buried. Every click emitter
 *   passes the whole match object (`result: match`), and `VisualMatch.price` is
 *   a `{ value, extracted_value, currency }` triple that no sink flattens. So
 *   price is DERIVED centrally in `useAnalytics.track`, exactly like
 *   `merchant_key` (see merchantKey.ts) — one derivation, eighteen emitters
 *   fixed, no call sites touched.
 *
 * - **The click identifier is not derivable**, because it does not exist yet at
 *   click time. `link_clicks.short_id` is minted server-side when the browser
 *   reaches `/redirect`, which is *after* the analytics event fires. So the
 *   client mints its own `click_id` up front and puts the SAME value on both
 *   rails — the analytics event and the redirect URL (`cid`) — and the redirect
 *   controller stores it as `link_clicks.metadata.client_click_id`. That is the
 *   hop that makes PostHog → revenue joinable:
 *
 *       PostHog `Dupe Result Clicked`.click_id
 *         → link_clicks.metadata->>'client_click_id'
 *         → link_clicks.short_id
 *         → strackr_transactions.lc_short_id            ← NEVER `.user_id`
 *
 *   The last hop is the one people get wrong; see
 *   docs/references/lifecycle-event-contract.md.
 *
 * Everything here is ADDITIVE. No existing property is renamed or overwritten —
 * live experiment metrics filter on the legacy keys.
 */

/**
 * Payload keys that may hold a result-shaped object carrying a price, in
 * priority order. `result` is what the dupe grid and the chat cards pass;
 * `match` and `product` cover the surfaces that named it differently.
 */
const PRICE_OBJECT_KEYS = ['result', 'match', 'product'] as const

/**
 * ISO-4217 only: exactly three letters, case-insensitive.
 *
 * Deliberately NOT symbols. `$` is at least five different currencies, so a
 * symbol that survived into the property would let a GBP price be summed with
 * a USD one and read as a single number. Anything that isn't a three-letter
 * code is dropped, and the price is reported without a currency — which a
 * consumer can detect, unlike a wrong one.
 */
const CURRENCY_RE = /^[A-Za-z]{3}$/

/**
 * Longest `click_id` we mint or accept. Kept short because the value rides a
 * URL and lands in `link_clicks.metadata` jsonb on the highest-volume write
 * path in the app.
 */
export const MAX_CLICK_ID_LENGTH = 36

/**
 * Character class for a `click_id`, shared by the minter (web) and the
 * validator (api). Deliberately the same class `link_clicks.short_id` uses, so
 * the two identifiers can be eyeballed side by side in a metadata blob.
 */
const CLICK_ID_RE = new RegExp(`^[a-zA-Z0-9_.-]{1,${MAX_CLICK_ID_LENGTH}}$`)

/**
 * The redirect controller embeds `link_clicks.short_id` into every resolved
 * affiliate URL as the literal wrapper `DUPE<shortId>DUPE`, whatever the
 * network's sub-id param happens to be called (CJ `sid`, Rakuten `u1`, Impact
 * `subId1`, AvantLink `ctc`, Partnerize `pubref:` path segment, …). Matching
 * the wrapper is therefore network-agnostic — far more robust than enumerating
 * every network's param name.
 */
const SHORT_ID_RE = /DUPE([A-Za-z0-9_.-]{1,64})DUPE/

/**
 * Extract the dupe `short_id` from a RESOLVED affiliate redirect URL, or null
 * when the URL carries none (a non-monetized/raw search URL never created a
 * `link_clicks` row).
 *
 * Only the handful of surfaces that pre-resolve their redirect client-side
 * (the instant-search broadcast) ever hold such a URL. Everywhere else the
 * browser navigates straight to `/redirect?link=…` and the short_id is minted
 * after the page is gone — which is the entire reason `click_id` exists.
 */
export function parseShortIdFromRedirectUrl(
  url: string | null | undefined
): string | null {
  if (!url) return null
  return SHORT_ID_RE.exec(url)?.[1] ?? null
}

/**
 * Mint a fresh client-side click identifier. Client-only: `crypto.randomUUID`
 * is not guaranteed on every runtime we render in, so fall back to a random
 * base-36 string of the same character class.
 *
 * ⚠️ Never call this during render on a server-rendered surface. The value goes
 * into an `href`, so a server-minted id would differ from the client's and
 * produce a hydration mismatch. Mint it in an effect (see `useClickId`).
 */
export function mintClickId(): string {
  const cryptoRef =
    typeof globalThis !== 'undefined'
      ? (globalThis.crypto as Crypto | undefined)
      : undefined

  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return cryptoRef.randomUUID()
  }

  return `c${Math.random().toString(36).slice(2)}${Math.random()
    .toString(36)
    .slice(2)}`.slice(0, MAX_CLICK_ID_LENGTH)
}

/**
 * Server-side gate for a `cid` arriving on a redirect URL. Anything that isn't
 * the exact shape we mint is dropped rather than truncated — a mangled id joins
 * to nothing, and a truncated one could collide with a real one.
 */
export function sanitizeClickId(
  value: string | null | undefined
): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return CLICK_ID_RE.test(trimmed) ? trimmed : null
}

/**
 * Put a freshly minted `click_id` onto an outbound redirect URL, replacing any
 * `cid` already there.
 *
 * Called at ACTIVATION time, not render time. A card's href is built once and
 * can be activated many times — double-click, middle-click, browser back, open
 * in a new tab — so a `cid` baked in at render would end up on several
 * `link_clicks` rows at once. The join is then many-to-many and one analytics
 * event can claim several clicks' worth of revenue. Minting per activation and
 * rewriting the URL in the same event handler keeps it strictly 1:1.
 *
 * Handles relative URLs (`/redirect?link=…`) as well as absolute ones, and
 * returns the input unchanged if it cannot be parsed — a click that navigates
 * is always better than a click that is measured.
 */
export function withClickId(
  url: string,
  clickId: string,
  base?: string
): string {
  if (!url || !sanitizeClickId(clickId)) return url

  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url)
    const parsed = new URL(
      url,
      isAbsolute ? undefined : (base ?? 'https://dupe.com')
    )
    parsed.searchParams.set('cid', clickId)
    return isAbsolute
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}

/** Normalized price properties added to an event, or null when none derivable. */
export interface DerivedPriceProps {
  /** Numeric price of the clicked/viewed product, in `price_currency`. */
  price: number
  /** ISO-4217 code when the payload carried one, else null. */
  price_currency: string | null
}

/**
 * Parse a display price string into a number.
 *
 * The hard part is that `,` and `.` swap roles between locales, and getting it
 * wrong is not a rounding error — it is a factor of 1000, silently, in the
 * direction that makes a sofa look like pocket change.
 *
 * The rule, applied to the LAST separator in the string:
 *
 *   exactly three digits after it, and at least one digit before it
 *     → it is a THOUSANDS separator      `$1,299` → 1299   `1.299` → 1299
 *   otherwise
 *     → it is the DECIMAL separator      `12,99` → 12.99   `$429.00` → 429
 *
 * The "exactly three digits" test is what makes this safe in both locale
 * conventions: retail prices do not have three decimal places, so a
 * three-digit tail is always grouping. Everything before the last separator is
 * grouping by definition and gets stripped.
 *
 *   `1,299.00` → 1299    `1.299,00` → 1299    `12,345,678` → 12345678
 *   `45.5` → 45.5        `0.75` → 0.75        `,500` → 0.5
 */
export const parsePriceValue = (raw: unknown): number | null => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw !== 'string') return null

  // '$1,299.00' / '£45' / 'US$12.50' — drop everything that isn't part of the
  // number itself.
  const cleaned = raw.replace(/[^0-9.,-]/g, '')
  if (!cleaned) return null

  const lastSepIndex = Math.max(
    cleaned.lastIndexOf(','),
    cleaned.lastIndexOf('.')
  )

  let normalized: string
  if (lastSepIndex === -1) {
    normalized = cleaned
  } else {
    const tail = cleaned.slice(lastSepIndex + 1)
    const head = cleaned.slice(0, lastSepIndex)
    const isGrouping =
      /^\d{3}$/.test(tail) && /\d/.test(head) && !/^-?0+$/.test(head)

    normalized = isGrouping
      ? cleaned.replace(/[.,]/g, '')
      : `${head.replace(/[.,]/g, '')}.${tail}`
  }

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

const toCurrency = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim().toUpperCase()
  return CURRENCY_RE.test(trimmed) ? trimmed : null
}

/**
 * Sibling fields that already hold a PARSED number for the price next to them.
 * `VisualMatch.price` nests it as `extracted_value`; `ResearchBuyLink` puts
 * `extractedPrice` beside a display-string `price`. Always prefer these — a
 * number upstream already got right beats re-parsing its display form.
 */
const EXTRACTED_PRICE_KEYS = [
  'extractedPrice',
  'extracted_price',
  'extracted_value',
] as const

const extractedNumberFrom = (obj: Record<string, unknown>): number | null => {
  for (const key of EXTRACTED_PRICE_KEYS) {
    const value = obj[key]
    if (typeof value !== 'number') continue
    const parsed = parsePriceValue(value)
    if (parsed !== null) return parsed
  }
  return null
}

/**
 * Read a price off a result-shaped object, in preference order:
 *
 *  1. a parsed number sitting beside it (`extractedPrice` / `extracted_value`)
 *  2. the nested `{ value, extracted_value, currency }` VisualMatch triple
 *  3. the display string, parsed
 */
const priceFromResultObject = (raw: unknown): DerivedPriceProps | null => {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const priceField = obj.price

  const currencyBeside = toCurrency(obj.currency ?? obj.price_currency)

  // A sibling parsed number wins over the display string next to it.
  const sibling = extractedNumberFrom(obj)
  if (sibling !== null) {
    return { price: sibling, price_currency: currencyBeside }
  }

  if (typeof priceField === 'number' || typeof priceField === 'string') {
    const price = parsePriceValue(priceField)
    if (price === null) return null
    return { price, price_currency: currencyBeside }
  }

  if (!priceField || typeof priceField !== 'object') return null
  const priceObj = priceField as Record<string, unknown>

  const price = extractedNumberFrom(priceObj) ?? parsePriceValue(priceObj.value)
  if (price === null) return null

  return { price, price_currency: toCurrency(priceObj.currency) }
}

/**
 * Resolve whatever price hint an event payload happens to carry into ONE
 * numeric `price` + `price_currency` pair.
 *
 * Returns null when the payload carries no PARSEABLE price — a blocked/walled
 * card, a "See price" placeholder, or an event that isn't about a product at
 * all. A genuine `0` is a price and is kept as `0`; only the unknown case is
 * null. Callers must treat "absent" as unknown, never as zero.
 */
export function derivePriceProps(
  properties: Record<string, unknown> | null | undefined
): DerivedPriceProps | null {
  if (!properties) return null

  // A top-level numeric `price` is already the contract — leave it alone and
  // only fill in the currency alongside it.
  const existing = properties.price
  if (typeof existing === 'number' && Number.isFinite(existing)) {
    return {
      price: existing,
      price_currency: toCurrency(
        properties.price_currency ?? properties.currency
      ),
    }
  }

  for (const key of PRICE_OBJECT_KEYS) {
    const derived = priceFromResultObject(properties[key])
    if (derived) return derived
  }

  // Some surfaces flatten the match onto the payload itself.
  return priceFromResultObject(properties)
}

/**
 * Resolve the outbound click identifier for an event payload.
 *
 * Prefers an explicit `click_id` (the minted rail — present wherever a card
 * built its own redirect URL), then falls back to a `short_id` recovered from
 * an already-resolved affiliate URL. The fallback only fires on pre-resolving
 * surfaces, so it is a bonus, not the mechanism.
 */
export function deriveClickIdProps(
  properties: Record<string, unknown> | null | undefined
): { click_id?: string; short_id?: string } | null {
  if (!properties) return null

  const out: { click_id?: string; short_id?: string } = {}

  const clickId = sanitizeClickId(properties.click_id as string | undefined)
  if (clickId) out.click_id = clickId

  const existingShortId =
    typeof properties.short_id === 'string' ? properties.short_id : null
  const shortId =
    existingShortId ??
    parseShortIdFromRedirectUrl(
      (typeof properties.resolvedUrl === 'string'
        ? properties.resolvedUrl
        : null) ??
        (typeof properties.redirectUrl === 'string'
          ? properties.redirectUrl
          : null) ??
        (typeof properties.link === 'string' ? properties.link : null)
    )
  if (shortId) out.short_id = shortId

  return out.click_id || out.short_id ? out : null
}
